/**
 * cache.js - Data caching and request management utilities
 */

/**
 * DataCache - Simple in-memory cache with TTL
 */
export class DataCache {
  constructor(defaultTTL = 60000) {
    this.cache = new Map();
    this.defaultTTL = defaultTTL;
  }

  set(key, value, ttl = this.defaultTTL) {
    const expiresAt = Date.now() + ttl;
    this.cache.set(key, { value, expiresAt });
  }

  get(key) {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    return item.value;
  }

  delete(key) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }

  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;
    
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
}

/**
 * RequestManager - Handles API requests with deduplication
 */
export class RequestManager {
  constructor() {
    this.pendingRequests = new Map();
  }

  /**
   * Fetch with automatic request deduplication
   */
  async fetch(url, options = {}) {
    const requestKey = `${url}_${JSON.stringify(options)}`;

    if (this.pendingRequests.has(requestKey)) {
      console.log(`[RequestManager] Reusing pending request for: ${url}`);
      return this.pendingRequests.get(requestKey);
    }

    const requestPromise = this._executeRequest(url, options)
      .finally(() => {
        this.pendingRequests.delete(requestKey);
      });

    this.pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  /**
   * Internal method to execute the actual fetch request
   */
  async _executeRequest(url, options = {}) {
    try {
      const fetchOptions = {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      console.log(`[RequestManager] Fetching: ${url}`);
      
      const response = await fetch(url, fetchOptions);
      return response;
      
    } catch (error) {
      console.error(`[RequestManager] Request failed for ${url}:`, error);
      throw error;
    }
  }

  cancelAll() {
    this.pendingRequests.clear();
  }

  getPendingCount() {
    return this.pendingRequests.size;
  }
}

/**
 * Simple retry wrapper for failed requests
 */
export async function fetchWithRetry(
  url,
  options = {},
  maxRetries = 3,
  retryDelay = 1000
) {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        ...options,
      });

      if (response.ok) {
        return response;
      }

      // Don't retry client errors (4xx)
      if (response.status >= 400 && response.status < 500) {
        return response;
      }

      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
      
    } catch (error) {
      lastError = error;
      console.warn(`[fetchWithRetry] Attempt ${attempt + 1} failed:`, error.message);
    }

    // Wait before retrying (exponential backoff)
    if (attempt < maxRetries - 1) {
      const delay = retryDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error('Request failed after retries');
}
